import React, { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatProvider, useChatContext } from '../context/ChatContext';
import { AccessibilityProvider, useKeyboardShortcuts, useAnnouncer } from '../context/AccessibilityContext';
import { UserInteractionProvider } from '../context/UserInteractionContext';
import LayoutManager from './LayoutManager';
import ChatContainer from './ChatContainer';
import { IntroContent } from './IntroContent';
import { ErrorBoundary } from './ErrorBoundary';

import { LanguageSelector } from './LanguageSelector';
import { ChatAppProps } from '../types';
import { KEYBOARD_KEYS } from '../utils/accessibility';
import { useChatConnection } from '../hooks/useChatConnection';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import './ChatApp.css';
import './ErrorBoundary.css';
import '../styles/accessibility.css';

// Inner component that uses the context
const ChatAppInner: React.FC<Omit<ChatAppProps, 'mode' | 'onModeChange'> & { sseEndpoint: string }> = React.memo(({
  sseEndpoint,
  introContent,
  theme = 'light',
  language,
  onThemeChange,
  onLanguageChange,
  showLanguageSelector = false,
  showThemeToggle = false,
}) => {
  const { state, dispatch } = useChatContext();
  const shortcutManager = useKeyboardShortcuts();
  const announcer = useAnnouncer();
  const { t, i18n } = useTranslation();
  const cleanupRef = useRef<(() => void)[]>([]);

  // Initialize language if provided
  useEffect(() => {
    if (language && language !== i18n.language) {
      i18n.changeLanguage(language).catch(console.error);
    }
  }, [language, i18n]);

  // Apply theme to document
  useEffect(() => {
    const applyTheme = (themeValue: 'light' | 'dark' | 'auto') => {
      const root = document.documentElement;
      
      if (themeValue === 'auto') {
        // Use system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      } else {
        root.setAttribute('data-theme', themeValue);
      }
    };

    applyTheme(theme);

    // Listen for system theme changes when in auto mode
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('auto');
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);
  
  // Set up SSE connection only (no WebSocket unless voice is needed)
  const chatConnection = useChatConnection({
    sseEndpoint: sseEndpoint,
    wsEndpoint: '', // Disable WebSocket by default
  });

  // Load initial message on app start (without creating SSE connection)
  useEffect(() => {
    const loadInitialMessage = async () => {
      try {
        const baseUrl = sseEndpoint.replace('/api/chat/stream', '');
        const response = await fetch(`${baseUrl}/api/chat/initial`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const initialMessage = {
              ...result.data,
              timestamp: new Date(result.data.timestamp),
            };
            dispatch({ type: 'SET_INITIAL_MESSAGE', payload: initialMessage });
            console.log('📬 Initial message loaded without SSE connection');
          }
        }
      } catch (error) {
        console.error('Failed to load initial message:', error);
      }
    };

    // Only load if no messages exist
    if (state.messages.length === 0) {
      loadInitialMessage();
    }
  }, [sseEndpoint, state.messages.length, dispatch]);

  // Set up audio playback
  const { playbackControls, playbackState } = useAudioPlayback();

  // Memoize mode change handler separately to avoid recreating all handlers
  const handleModeChange = useCallback((mode: 'head' | 'popup' | 'fullscreen') => {
    // Update the context state when mode changes
    if (mode !== state.layoutMode) {
      dispatch({ type: 'SET_LAYOUT_MODE', payload: mode });
      announcer.announce(t('layout.switchedToMode', { mode }), 'polite');
    }
  }, [state.layoutMode, dispatch, announcer, t]);
  
  const handleConnectionError = useCallback((error: Error) => {
    console.error('Connection error:', error);
    announcer.announce(t('connection.connectionError'), 'assertive');
  }, [announcer, t]);
  
  const handleAudioError = useCallback((error: Error) => {
    console.error('Audio error:', error);
    announcer.announce(t('audio.playbackFailed'), 'polite');
  }, [announcer, t]);

  // Handle audio playback for new messages
  useEffect(() => {
    const latestMessage = state.messages[state.messages.length - 1];
    if (latestMessage?.voice && latestMessage.sender !== state.currentUser) {
      // Queue audio for automatic playback
      dispatch({ type: 'QUEUE_AUDIO', payload: latestMessage.voice });
    }
  }, [state.messages, state.currentUser, dispatch]);

  // Process audio queue - handled automatically by useAudioPlayback hook
  // The hook processes messages and manages audio playback automatically

  // Set up keyboard shortcuts
  useEffect(() => {
    // Clear existing shortcuts
    shortcutManager.clear();

    // Register keyboard shortcuts
    shortcutManager.register(KEYBOARD_KEYS.ESCAPE, [], () => {
      // Close modals, cancel recording, etc.
      if (state.isRecording) {
        dispatch({ type: 'STOP_RECORDING' });
        announcer.announce(t('voice.recordingCancelled'), 'polite');
      }
      // Stop audio playback
      if (playbackState.isPlaying) {
        playbackControls.stop();
        announcer.announce(t('audio.playbackStopped'), 'polite');
      }
    });

    shortcutManager.register('m', ['ctrl'], () => {
      // Cycle through layout modes
      const nextMode = state.layoutMode === 'head' ? 'popup' : 
                      state.layoutMode === 'popup' ? 'fullscreen' : 'head';
      handleModeChange(nextMode);
    });

    shortcutManager.register('/', ['ctrl'], () => {
      // Focus message input
      const messageInput = document.querySelector('.message-input__textarea') as HTMLTextAreaElement;
      if (messageInput) {
        messageInput.focus();
        announcer.announce(t('accessibility.focusMessageInput'), 'polite');
      }
    });

    // Add cleanup function
    const cleanup = () => {
      shortcutManager.clear();
    };
    cleanupRef.current.push(cleanup);

    return cleanup;
  }, [shortcutManager, state.isRecording, state.layoutMode, dispatch, announcer, handleModeChange, playbackState.isPlaying, playbackControls, t]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop audio playback
      if (playbackState.isPlaying) {
        playbackControls.stop();
      }
      // Note: SSE connection cleanup is handled by the useSSEConnection hook itself
      // Run all cleanup functions
      cleanupRef.current.forEach(cleanup => cleanup());
    };
  }, [playbackState.isPlaying, playbackControls]);

  // Check if we're in embed mode
  const isEmbedMode = !!(window as any).chatWidgetConfig;

  return (
    <div className="chat-app" role="application" aria-label={t('app.title')}>
      {/* Skip links for keyboard navigation - only show in standalone mode */}
      {!isEmbedMode && (
        <>
          <a href="#main-content" className="skip-link">
            {t('app.skipToMainContent')}
          </a>
          <a href="#message-input" className="skip-link">
            {t('app.skipToMessageInput')}
          </a>
        </>
      )}
      
      <LayoutManager
        mode={state.layoutMode}
        onModeChange={handleModeChange}
      >
        {/* Language Controls - only show if enabled */}
        {showLanguageSelector && (
          <div className="chat-app__controls">
            <LanguageSelector
              compact={state.layoutMode === 'head'}
            />
          </div>
        )}
        
        <ChatContainer 
          onRetryConnection={undefined}
          onSendMessage={chatConnection.sendTextMessage}
          onVoiceStart={undefined}
          onVoiceStop={undefined}
          onVoiceData={undefined}
        />
      </LayoutManager>
      {state.showIntro && introContent && (
        <IntroContent content={introContent} />
      )}
    </div>
  );
});

// Main ChatApp component with provider
const ChatApp: React.FC<ChatAppProps> = ({
  mode,
  onModeChange,
  sseEndpoint,
  introContent,
  theme,
  language,
  onThemeChange,
  onLanguageChange,
  showLanguageSelector,
  showThemeToggle,
}) => {
  const { t } = useTranslation();
  const handleError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    console.error('ChatApp Error:', error, errorInfo);
    // Could send to error reporting service here
  }, []);

  return (
    <ErrorBoundary onError={handleError}>
      <UserInteractionProvider>
        <AccessibilityProvider>
          <ErrorBoundary 
            onError={handleError}
            fallback={
              <div className="chat-app-error" role="alert">
                <h2>{t('errors.chatUnavailable')}</h2>
                <p>{t('errors.refreshPage')}</p>
                <button onClick={() => window.location.reload()}>
                  {t('errors.refreshButton')}
                </button>
              </div>
            }
          >
            <ChatProvider initialMode={mode}>
              <ErrorBoundary onError={handleError}>
                <ChatAppInner
                  sseEndpoint={sseEndpoint || 'http://localhost:3001/api/chat/stream'}
                  introContent={introContent}
                  theme={theme}
                  language={language}
                  onThemeChange={onThemeChange}
                  onLanguageChange={onLanguageChange}
                  showLanguageSelector={showLanguageSelector}
                  showThemeToggle={showThemeToggle}
                />
              </ErrorBoundary>
            </ChatProvider>
          </ErrorBoundary>
        </AccessibilityProvider>
      </UserInteractionProvider>
    </ErrorBoundary>
  );
};

export default ChatApp;